import { useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import './DropdownList.css';

export interface DropdownListOption<T> {
  value: T;
  label: string;
  key: string;
}

interface DropdownListProps<T> {
  triggerRef: React.RefObject<HTMLElement | null>;
  open: boolean;
  options: DropdownListOption<T>[];
  highlightIndex: number;
  onSelect: (value: T) => void;
  onHighlightChange: (index: number) => void;
  id?: string;
  /** Прокручивать список к подсвеченному пункту при смене highlight с клавиатуры/открытии (наведение мыши никогда не прокручивает) */
  scrollHighlightIntoView?: boolean;
  /** Ширина по контенту списка, а не по ширине триггера */
  widthByContent?: boolean;
  /** Выровнять по центру триггера по горизонтали */
  alignCenter?: boolean;
  /** Кастомный рендер опции в списке */
  renderOption?: (opt: DropdownListOption<T>) => React.ReactNode;
  /** Доп. класс на корневой список (портал) — для темизации потребителя */
  className?: string;
}

export function DropdownList<T>({
  triggerRef,
  open,
  options,
  highlightIndex,
  onSelect,
  onHighlightChange,
  id,
  scrollHighlightIntoView = true,
  widthByContent,
  alignCenter,
  renderOption,
  className: listClassName,
}: DropdownListProps<T>) {
  const listRef = useRef<HTMLElement | null>(null);
  const hostRefForPosition = useRef<HTMLElement | null>(null);
  /** Следующее изменение highlight пришло с пункта под курсором — не вызывать scrollIntoView */
  const skipScrollFromMouseRef = useRef(false);
  const positionStyle = useDropdownPosition(triggerRef, open, {
    widthByContent,
    alignCenter,
    dropdownRef: hostRefForPosition,
  });
  const style = widthByContent
    ? { ...positionStyle, width: 'max-content' as const, minWidth: 'max-content' }
    : positionStyle;

  const handleOptionMouseEnter = useCallback(
    (index: number) => {
      skipScrollFromMouseRef.current = true;
      onHighlightChange(index);
    },
    [onHighlightChange]
  );

  useEffect(() => {
    if (!open || !scrollHighlightIntoView) return;
    if (skipScrollFromMouseRef.current) {
      skipScrollFromMouseRef.current = false;
      return;
    }
    const el = listRef.current?.children[highlightIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex, open, scrollHighlightIntoView]);

  if (!open || options.length === 0) return null;

  return createPortal(
    <OverlayScrollArea
      defer={false}
      contentRef={listRef}
      hostRefForPosition={hostRefForPosition}
      element="ul"
      id={id}
      className={`dropdown-list ${widthByContent ? 'dropdown-list_width-by-content' : ''} ${listClassName ?? ''}`.trim()}
      role="listbox"
      data-combo-dropdown
      style={style}
    >
      {options.map((opt, i) => (
        <li
          key={opt.key}
          id={id ? `${id}-opt-${i}` : undefined}
          role="option"
          aria-selected={i === highlightIndex}
          className={`dropdown-list__option ${i === highlightIndex ? 'dropdown-list__option_highlight' : ''}`}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(opt.value);
          }}
          onMouseEnter={() => handleOptionMouseEnter(i)}
        >
          {renderOption ? renderOption(opt) : opt.label}
        </li>
      ))}
    </OverlayScrollArea>,
    document.body
  );
}
